import React, { useEffect, useRef } from 'react';
import _classNames from 'classnames';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeSelectProfile } from 'containers/Auth/selectors';
import {
  makeSelectTakenAssessments,
  makeSelectSavedAssessments,
  makeSelectProcessing as makeSelectAssessmentsProcessing,
} from 'containers/Assessments/selectors';
import {
  makeSelectSavedResources,
  makeSelectUserHistory,
  makeSelectProcessing as makeSelectResourcesProcessing,
} from 'containers/Resources/selectors';
import {
  getSavedAssessments,
  getTakenAssessments,
} from 'containers/Assessments/actions';
import { getSavedTopics } from 'containers/Topics/actions';
import {
  makeSelectSavedTopics,
  makeSelectProcessing as makeSelectTopicsProcessing,
} from 'containers/Topics/selectors';
import {
  getSavedResources,
  getUserHistory,
} from 'containers/Resources/actions';
import AppContainer from 'components/AppContainer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useIsOnboardingEnabled } from 'components/Personalizations/hooks/useIsOnboardingEnabled';
import Mixpanel from 'utils/mixpanelService';
import ArrowIcon from 'components/DiscoverLink/right-arrow.svg';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: props => props.bgColor || '#F8F9F9',
    display: 'flex',
    height: 121,
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      paddingTop: '16px !important',
      paddingBottom: '16px !important',
    },
  },
  iconWrapper: {
    height: 70,
    width: 'auto',
    marginRight: 16,
    [theme.breakpoints.down('sm')]: {
      width: 50,
      height: 'auto',
    },
  },
  icon: {
    height: '100%',
    width: '100%',
  },
  text: {
    color: theme.palette.text.primary,
    [theme.breakpoints.down('1100')]: {
      fontSize: '0.875rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.063rem',
    },
  },
  link: {
    color: theme.palette.primary.main,
    fontSize: 14,
    lineHeight: '18px',
    fontFamily: 'MadaRegular',
    '&:hover': {
      opacity: 0.8,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  arrow: {
    marginLeft: 5,
  },
  grayscaleBackground: {
    backgroundColor: props => props.bgColor || '#F8F8F8',
  },
  grayscaleImage: {
    filter: 'grayscale(1)',
  },
}));

const stateSelector = createStructuredSelector({
  takenAssessments: makeSelectTakenAssessments(),
  savedResources: makeSelectSavedResources(),
  savedAssessments: makeSelectSavedAssessments(),
  savedTopics: makeSelectSavedTopics(),
  profile: makeSelectProfile(),
  userHistory: makeSelectUserHistory(),
  assessmentsProcessing: makeSelectAssessmentsProcessing(),
  resourcesProcessing: makeSelectResourcesProcessing(),
  topicsProcessing: makeSelectTopicsProcessing(),
});

function SignedInBanner({ grayscale, bgColor }) {
  const classes = useStyles({ bgColor });
  const dispatch = useDispatch();
  const {
    profile,
    savedResources,
    takenAssessments,
    savedAssessments,
    savedTopics,
    userHistory,
    assessmentsProcessing,
    resourcesProcessing,
    topicsProcessing,
  } = useSelector(stateSelector);
  const initialLoaded = useRef(false);
  const [bannerSiteCopy] = useSiteCopySelector(['banner-landing']);
  const location = useLocation();
  const isOnboardingEnabled = useIsOnboardingEnabled();

  useEffect(() => {
    if (!profile.isEmpty) {
      dispatch(getSavedTopics());
      dispatch(getSavedAssessments());
      dispatch(getTakenAssessments());
      dispatch(getSavedResources());
      dispatch(getUserHistory());
    }
  }, [profile]);

  if (
    !initialLoaded.current &&
    (assessmentsProcessing || resourcesProcessing || topicsProcessing)
  )
    return null;

  const isOnboardingEmpty =
    !profile.isEmpty && _isEmpty(_get(profile, 'onboarding', null));
  const isOnboardingCompleted = !!_get(profile, 'onboarding.topics');
  const items = _get(
    bannerSiteCopy,
    !isOnboardingEnabled || isOnboardingEmpty || isOnboardingCompleted
      ? 'pageCopy.onboardingComplete.items'
      : 'pageCopy.onboardingIncomplete.items',
    [],
  );

  if (profile.isEmpty || _isEmpty(items)) return null;

  if (!initialLoaded.current) initialLoaded.current = true;

  const track = url => () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName === '/' ? 'homePage' : `${pageName}Page`;

    Mixpanel.track(`Banner Call To Action Clicked`, {
      page: finalPageName,
      url,
    });
  };

  const assessmentsCount = takenAssessments.length;
  const resourcesCount =
    savedResources.length + savedTopics.length + savedAssessments.length;
  const historyCount = userHistory.length;

  const finalItems = items.map(item => {
    const { callToAction, label, noItemsUrl, url } = item;
    let count = 0;

    if (item.type === 'assessments') count = assessmentsCount;
    else if (item.type === 'resources') count = resourcesCount;
    else if (item.type === 'history') count = historyCount;

    let finalLabel;
    if (count === 0) {
      finalLabel = item.noItemsLabel;
    } else {
      finalLabel = `${count} ${label}`;
    }

    const preUrl = count === 0 ? noItemsUrl || url : url;
    const finalUrl = preUrl.includes('/onboarding/start')
      ? '/onboarding/start?onboarding_path=banner'
      : preUrl;

    let finalCallToAction = callToAction;
    if (count === 0) {
      finalCallToAction = item.noItemsCallToAction;
    }

    return {
      ...item,
      count,
      label: finalLabel,
      callToAction: finalCallToAction,
      url: finalUrl,
    };
  });

  return (
    <Grid
      item
      xs={12}
      className={_classNames(classes.root, {
        [classes.grayscaleBackground]: grayscale,
      })}
    >
      <AppContainer align="center">
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {finalItems.map(item => (
              <Grid
                item
                xs={12}
                sm={6}
                md
                className={classes.wrapper}
                key={item.label}
              >
                <div className={classes.iconWrapper}>
                  <img
                    src={item.icon}
                    className={_classNames(classes.icon, {
                      [classes.grayscaleImage]: grayscale,
                    })}
                    alt=""
                  />
                </div>
                <Box component="div">
                  <Typography
                    component="p"
                    className={classes.text}
                    variant="h6"
                  >
                    {item.label}
                  </Typography>
                  {item.callToAction && (
                    <Link
                      to={item.url}
                      className={classes.link}
                      onClick={track(item.url)}
                    >
                      {item.callToAction}
                      <img
                        src={ArrowIcon}
                        alt="Link"
                        className={classes.arrow}
                      />
                    </Link>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </AppContainer>
    </Grid>
  );
}

export default SignedInBanner;
